import { Type } from 'class-transformer';
import dayjs from 'dayjs';
import { Attachment, ModelWithAttachments } from './attachment';
import type { Direction } from './activity';
import { EmailSender } from './email_sender';
import { EmailThread } from './email_thread';
import { Model } from './model';
import { Classification } from './classification';
import { User } from './user';
import { Task } from './task';
import { TransformStringToDayJS } from './utils';

export enum EmailStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  SENDING = 'sending',
  SENT = 'sent',
}

export class Email extends ModelWithAttachments {
  @Type(() => User)
  author: User | null;

  author_id: string;

  bcc: string[];

  body: string | null;

  cc: string[];

  direction: Direction;

  email_reason: string;

  @Type(() => EmailSender)
  email_sender: EmailSender | null;

  email_sender_id: string | null;

  error_message: string | null;

  from_email: string | null;

  @Type(() => Classification)
  classifications: Classification[] | null;

  parent_email_id: string | null;

  forwarded_from_email_id: string | null;

  @Type(() => EmailRemoteLink)
  remote_links: EmailRemoteLink[];

  reply_to: string[];

  sent_by_id: string | null;

  @Type(() => User)
  sent_by: User | null;

  @TransformStringToDayJS()
  sent_at: dayjs.Dayjs | null;

  snippet: string | null;

  status: EmailStatus;

  subject: string;

  @Type(() => Task)
  tasks: Task[] | null;

  @Type(() => EmailThread)
  thread: EmailThread | null = null;

  thread_id: string;

  to: string[];

  processing_status?: string;

  get isProcessing(): boolean {
    return !!this.processing_status && this.processing_status !== 'completed';
  }

  get processingLabel(): string {
    switch (this.processing_status) {
      case 'processing':
        return 'Processing email...';
      case 'classified':
        return 'Analyzed, creating tasks...';
      case 'task_created':
        return 'Task created';
      default:
        return 'Processing email...';
    }
  }

  get isSent(): boolean {
    return this.status === EmailStatus.SENT;
  }

  get isDraft(): boolean {
    return this.status === EmailStatus.DRAFT;
  }

  get isSending(): boolean {
    return this.status === EmailStatus.PENDING || this.status === EmailStatus.SENDING;
  }

  hasRemoteLink(emailSender: EmailSender): boolean {
    if (!this.remote_links) return false;
    for (const remoteLink of this.remote_links) {
      if (remoteLink.email_sender_id === emailSender.id) return true;
    }

    return false;
  }

  hasRemoteLinkInChain(emailSender: EmailSender, thread: EmailThread): boolean {
    if (this.hasRemoteLink(emailSender)) return true;
    if (!this.parent_email_id) return false;
    const parent = thread.getEmailById(this.parent_email_id);
    return parent?.hasRemoteLinkInChain(emailSender, thread) ?? false;
  }

  getParticipants(): string[] {
    const participants = new Set<string>();
    for (const to of this.to) {
      participants.add(to);
    }
    for (const cc of this.cc) {
      participants.add(cc);
    }
    if (this.from_email) {
      participants.add(this.from_email);
    }
    return Array.from(participants);
  }

  get attachmentIds() {
    return this.attachments?.map((att) => att.id)?.filter(Boolean) || [];
  }

  get attachableInvoices() {
    return this.thread?.invoicesWithPdfIds?.filter(
      (inv) => !this.attachmentIds.includes(inv.pdf_id || ''),
    );
  }

  get openTasks() {
    return this.tasks?.filter((task) => task.isOpen);
  }

  get completedTasks() {
    return this.tasks?.filter((task) => task.isCompleted);
  }

  get closedTasks() {
    return this.tasks?.filter((task) => task.isClosed);
  }

  get openResponseTasks() {
    return this.openTasks?.filter((task) => task.isReplyRequired);
  }

  get oldestOpenResponseTask() {
    return this.openResponseTasks?.sort((a, b) => a?.created_at.diff(b?.created_at))[0];
  }
}

export class EmailRemoteLink extends Model {
  email_id: string;

  email_sender_id: string;

  remote_id: string;

  remote_thread_id: string;
}
